<template>
  <div class="content mb-5">
    <b-row>
      <b-col sm="12" md="6">
        <b-form-group
          v-if="!loading"
          class="mb-3"
          label-for="domain"
          :label="this.$t('Domain')"
          :state="$validateState($v.form.domain)"
        >
          <b-form-input
            data-test-id="modal-domain-domain"
            id="domain"
            v-model="form.domain"
            @blur="setValidationInput('domain', $event.target.value)"
            :state="$validateState($v.form.domain)"
            :placeholder="this.$t('For example, www.example.com')"
          />

          <template #invalid-feedback>
            <p
              v-if="
                $v.form.domain.$dirty &&
                $v.form.domain.required &&
                !$v.form.domain.isQualifiedDomain
              "
            >
              {{
                $t(
                  "Please specify domain or partial domain only URLs are not permitted."
                )
              }}
            </p>
            <p v-if="!$v.form.domain.required">
              {{ $t("This is a required field.") }}
            </p>
          </template>
        </b-form-group>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="10%"
        ></b-skeleton>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12" md="6">
        <t-checkbox
          v-if="!loading"
          white-bg
          class="mt-2 mb-3"
          id="subdomains"
          data-test-id="modal-domain-subdomains"
          v-model="$v.form.subdomains.$model"
          :label="this.$t('Include subdomains')"
        />
        <b-skeleton
          v-else
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
      <b-col sm="12" md="6">
        <t-checkbox
          v-if="!loading"
          white-bg
          data-test-id="modal-domain-log-results"
          class="mt-2 mb-3"
          id="log_requests"
          v-model="$v.form.log_requests.$model"
          :label="this.$t('Log requests for reporting')"
        />
        <b-skeleton
          v-else
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <b-row>
      <b-col sm="12" md="6">
        <b-form-group
          v-if="!loading"
          :label="$t('Comments (optional)')"
          label-for="comment"
          :state="$validateState($v.form.comment)"
        >
          <b-form-textarea
            data-test-id="modal-domain-comment"
            id="comment"
            rows="3"
            max-rows="6"
            v-model="form.comment"
            :state="$validateState($v.form.comment)"
            @blur="setValidationInput('comment', $event.target.value)"
          ></b-form-textarea>
          <template #invalid-feedback>
            <p v-if="$v.form.comment.$dirty && !$v.form.comment.minLength">
              {{
                $t("X must be at least X characters long.", {
                  field: $t("Comments"),
                  length: $v.form.comment.$params.minLength.min,
                })
              }}
            </p>
          </template>
        </b-form-group>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="10%"
        ></b-skeleton>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="86px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <t-save-button
      data-test-id="domains-save-button"
      :form-model="$v.form"
      :fetching="fetching"
      @submit="onSubmit"
    />
  </div>
</template>
<route>
{
  "name": "MSP Global Domain Details"
}
</route>
<script>
import { required, minLength, maxLength } from "vuelidate/lib/validators";
import { mapGetters, mapActions } from "vuex";

export default {
  namespaced: true,
  layout: "msp",
  data() {
    return {
      loading: true,
      fetching: false,
      form: {
        domain: "",
        subdomains: false,
        log_requests: false,
        comment: "",
      },
    };
  },
  validations: {
    form: {
      domain: {
        required,
        minLength: minLength(2),
      },
      comment: {
        minLength: minLength(5),
        maxLength: maxLength(250),
      },
      subdomains: {},
      log_requests: {},
    },
  },
  methods: {
    ...mapActions("mspDomains", [
      "fetchMSPDomainListEntry",
      "updateMSPDomainListEntry",
    ]),
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapActions("toast", ["displayToast"]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async onSubmit() {
      this.fetching = true;
      try {
        await this.updateMSPDomainListEntry({
          id: this.mspId,
          action: this.selectedMSPDomain.object,
          domainId: this.selectedMSPDomain.id,
          policyId: this.selectedMSPDomain.policyid,
          body: this.form,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
        this.$v.form.$reset();
      }
    },
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser", "mspId"]),
    ...mapGetters("mspDomains", ["selectedMSPDomain"]),
  },
  watch: {
    selectedMSPDomain: {
      handler() {
        if (this.selectedMSPDomain) {
          this.form = {
            domain: this.selectedMSPDomain.domain,
            subdomains: this.selectedMSPDomain.subdomains,
            log_requests: this.selectedMSPDomain.log_requests,
            comment: this.selectedMSPDomain.comment || "",
          };
          this.setPageTitle(this.selectedMSPDomain.domain);
        }
      },
      immediate: true,
    },
  },
  async created() {
    await Promise.all([
      this.fetchMSPDomainListEntry({
        id: this.$route.query.msp_id,
        policyId: this.$route.query.p,
        action: this.$route.query.t,
        domainId: this.$route.params.id,
      }),
    ]);
    this.loading = false;
  },
};
</script>
